import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Empty, Field, Badge, ConfirmDialog, NativeSelect } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import ConflictDialog from "./ConflictDialog";
import { ShiftFields } from "./ShiftFields";
import { EditTimeButton, useAssignmentEdit } from "./AssignmentEdit";
import { eur, fmtDate, shiftSpan, todayISO } from "../lib/format";

function useAssignForm(open, worker, onDone) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  useEffect(() => {
    if (!open) return;
    setF({ site_id: "", start_date: todayISO(), end_date: todayISO(), shift: "" });
    api.get("/settings").then((r) => setF((p) => ({ ...p, shift: p.shift || r.data.default_shift })));
  }, [open]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setShift = (v) => setF((p) => ({ ...p, shift: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/assignments", { site_id: Number(f.site_id), start_date: f.start_date, end_date: f.end_date,
        worker_ids: [worker.id], shift: f.shift || null });
      toast.success(`✓ ${worker.full_name} assegnato`);
      onDone();
    } catch (err) {
      if (err.response?.status === 409) setConflict(err.response.data.detail.conflicts);
      else toast.error(`✕ ${errMsg(err)}`);
    } finally {
      setBusy(false);
    }
  };
  return { f, set, setShift, busy, conflict, setConflict, submit };
}

function AssignDialog({ open, onOpenChange, worker, sites, onSaved }) {
  const { f, set, setShift, busy, conflict, setConflict, submit } = useAssignForm(open, worker, () => { onOpenChange(false); onSaved(); });
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-popover max-w-xl" data-testid="worker-assign-form">
          <DialogHeader><DialogTitle className="font-heading">Nuova assegnazione · {worker.full_name}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
            <Field label="Cantiere *" className="sm:col-span-2">
              <NativeSelect required value={f.site_id || ""} onChange={set("site_id")} data-testid="worker-assign-site-select">
                <option value="" disabled>— Seleziona cantiere —</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}
              </NativeSelect>
            </Field>
            <Field label="Dal *"><Input type="date" required value={f.start_date || ""} onChange={set("start_date")} data-testid="worker-assign-start-input" /></Field>
            <Field label="Al *"><Input type="date" required min={f.start_date} value={f.end_date || ""} onChange={set("end_date")} data-testid="worker-assign-end-input" /></Field>
            <div className="sm:col-span-2"><ShiftFields value={f.shift || ""} onChange={setShift} testid="worker-assign-shift" /></div>
            <DialogFooter className="sm:col-span-2 gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button type="submit" disabled={busy || !shiftSpan(f.shift)} data-testid="worker-assign-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Assegna</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConflictDialog open={!!conflict} onOpenChange={() => setConflict(null)} items={conflict || []} />
    </>
  );
}

function PeriodBadge({ a, today }) {
  if (a.end_date < today) return <Badge className="bg-slate-800 border-slate-600 text-slate-300">Concluso</Badge>;
  if (a.start_date > today) return <Badge className="bg-sky-950 border-sky-600 text-sky-200">Programmato</Badge>;
  return <Badge className="bg-red-950 border-red-600 text-red-200">In corso</Badge>;
}

function AssignmentRow({ a, canDelete, onDelete, onEdit }) {
  const today = todayISO();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-3 border-b border-border/60 text-sm" data-testid={`worker-assignment-${a.id}`}>
      <div><Link to={`/cantieri/${a.site_id}`} className="font-semibold hover:text-amber-300">{a.site_name}</Link>
        <div className="text-xs text-muted-foreground num">{fmtDate(a.start_date)} → {fmtDate(a.end_date)} · {a.shift}</div></div>
      <div className="flex items-center gap-3">
        <PeriodBadge a={a} today={today} />
        {a.cost != null && <span className="num text-amber-300">{eur(a.cost)}</span>}
        {canDelete && <EditTimeButton onClick={() => onEdit(a)} testid={`worker-assignment-time-${a.id}`} />}
        {canDelete && <Button size="icon" variant="ghost" onClick={() => onDelete(a)} data-testid={`worker-assignment-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

export default function WorkerAssignments({ worker, reload }) {
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(null);
  const timeEdit = useAssignmentEdit(reload);
  useEffect(() => { api.get("/sites").then((r) => setSites(r.data.items.filter((s) => s.status !== "chiuso"))); }, []);
  const manageable = new Set(sites.map((s) => s.id));
  const remove = async () => {
    try {
      await api.delete(`/assignments/${del.id}`);
      toast.success("✓ Assegnazione eliminata");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDel(null);
  };
  return (
    <div data-testid="worker-assignments-panel">
      <div className="flex justify-end mb-2">
        <Button size="sm" onClick={() => setOpen(true)} disabled={!sites.length} data-testid="worker-assign-add-btn"><Plus className="h-4 w-4 mr-1" />Nuova assegnazione</Button>
      </div>
      {!worker.assignments.length ? <Empty title="Nessuna assegnazione" /> : worker.assignments.map((a) => (
        <AssignmentRow key={a.id} a={a} canDelete={manageable.has(a.site_id)} onDelete={setDel} onEdit={timeEdit.ask} />
      ))}
      {timeEdit.dialog}
      <AssignDialog open={open} onOpenChange={setOpen} worker={worker} sites={sites} onSaved={reload} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare questa assegnazione?"
        description={del ? `${del.site_name}: ${fmtDate(del.start_date)} → ${fmtDate(del.end_date)}` : ""} onConfirm={remove} />
    </div>
  );
}
